import type { NextPage, GetStaticProps } from 'next'
import { FormEvent, useEffect, useState } from 'react'
import Link from 'next/link'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { PrimaryButton, TertiaryButtonLink, Input } from 'ui'
import AuthLayout, { AuthBox, AuthFooter } from '../components/AuthLayout'
import AuthCodeScreen from '../components/AuthCodeScreen'

import getGlobalFlags from '../services/flags/getGlobalFlags'
import useAuth from '../hooks/useAuth'
import { useTracking } from '../services/context/TrackingContext'

import { usePreferredHomesRoute } from '../hooks/use-preferred-homes-route'

import { GoogleButtonIcon } from 'icons'

const SignIn: NextPage = () => {
  const { t } = useTranslation('auth')
  const { track } = useTracking()
  const {
    unverifiedAccount, 
    isAuthenticated,
    isLoading,
    signIn,
    errorMessage,
    federatedSignIn,
    authRedirect
  } = useAuth()

  const [showCodeStep, setShowCodeStep] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const router = useRouter()
  const { isReady } = router
  const { returnUrl } = router.query

  const homesRoute = usePreferredHomesRoute()

  // show code screen if user is not confirmed
  useEffect(() => {
    if (unverifiedAccount.email.length > 0) {

      setShowCodeStep(true)
    }
  }, [unverifiedAccount])

  useEffect(() => {
    if (isAuthenticated && isReady) {
      authRedirect()
    }
  }, [isAuthenticated, authRedirect, isReady])

  return (
    <AuthLayout
      mobileHeading={t('sign-in')}
      close={homesRoute}
      className="bg-white"
      hasFooter
      loading={isLoading || isAuthenticated}
      data-testid={isAuthenticated ? 'redirecting' : null}
    >
      <AuthBox>
        {showCodeStep ? (
          <AuthCodeScreen email={email} password={password} />
        ) : (
          <>
            <h1 className="hidden text-center hd-lg md:block">
              {t('sign-in')}
            </h1>
            <button
              className="mt-40 flex w-full items-center justify-center rounded-[4px] border-2 border-[#346EF1] py-[7px] px-20 text-[#346EF1] util-label"
              onClick={() => {
                federatedSignIn({ provider: 'Google' })
              }}
            >
              <GoogleButtonIcon className="rect-18" aria-hidden="true" />
              <span className="ml-24 block">{t('continue-with-google')}</span>
            </button>

            <div className="relative mt-24 border-t border-green-1">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-24 text-grey-6 util-label">
                {t('or')}
              </span>
            </div>

            <form
              className="mt-24 space-y-16"
              onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault()
                signIn({ email, password })
              }}
            >
              <Input
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                label={t('email')}
                id="email"
                type="email"
                placeholder={t('email')}
                error={!!errorMessage.signin}
                helperText={errorMessage.signin}
                autoComplete="email"
              />
              <Input
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.currentTarget.value)}
                label={t('password')}
                id="current-password"
                placeholder={t('password')}
                type="password"
                error={!!errorMessage.signin}
                helperText={
                  <Link href="/forgot-password" passHref>
                    <TertiaryButtonLink>
                      {t('forgot-password')}
                    </TertiaryButtonLink>
                  </Link>
                }
              />
              <AuthFooter>
                <PrimaryButton
                  type="submit"
                  className="mx-auto block w-full max-w-420 md:mr-0 md:w-auto"
                  disabled={!password || !email}
                >
                  {t('sign-in')}
                </PrimaryButton>
              </AuthFooter>
            </form>
            <footer className="mt-24 flex items-center justify-center space-x-14 text-14">
              <p>{t('need-an-account')}</p>

              <Link href={returnUrl ? `/sign-up?returnUrl=${returnUrl}` : '/sign-up'} passHref>
                <TertiaryButtonLink
                  onClick={() =>
                    track({
                      event: 'Sign Up Flow Opened',
                      properties: {
                        link_type: 'Text',
                        link_location: 'Sign In',
                        link_text: `${t('need-an-account')} ${t('sign-up')}`,
                      },
                    })
                  }
                >
                  {t('sign-up')}
                </TertiaryButtonLink>
              </Link>
            </footer>
          </>
        )}
      </AuthBox>
    </AuthLayout>
  )
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const getStaticProps: GetStaticProps = async ({ locale = '' }: any) => {
  const globalFlags = await getGlobalFlags()

  return {
    props: {
      ...globalFlags,
      ...(await serverSideTranslations(locale, ['common', 'auth'])),
    },
  }
}

export default SignIn
